import { useState, useEffect } from "react";
import { useNavigate } from "react-router";
import { Eye, EyeOff } from "lucide-react";
import loginBg from "../../login.png";
import { authentication, fetchUserPermissions } from "../../api/frameworksApi";
import { useAuthStore } from "../../stores/authStore";
import { useToastStore } from "../../stores/toastStore";
import { getErrorTitleAndMessage } from "../../api/errorMap";

function RegoShield({ size = 48 }: { size?: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 32 32" fill="none">
      <defs>
        <linearGradient id="login-shield-1" x1="0%" y1="100%" x2="100%" y2="0%">
          <stop offset="0%" stopColor="#2B378C" />
          <stop offset="51%" stopColor="#2E3285" />
          <stop offset="86%" stopColor="#372C77" />
          <stop offset="100%" stopColor="#3D2A70" />
        </linearGradient>
        <linearGradient id="login-shield-2" x1="100%" y1="0%" x2="0%" y2="100%">
          <stop offset="7%" stopColor="#882783" />
          <stop offset="50%" stopColor="#314196" />
          <stop offset="93%" stopColor="#364F9F" />
        </linearGradient>
        <radialGradient id="login-shield-3" cx="37%" cy="43%" r="81%">
          <stop offset="0%" stopColor="#261E58" />
          <stop offset="50%" stopColor="#455CA4" />
          <stop offset="100%" stopColor="#261E58" />
        </radialGradient>
        <radialGradient id="login-shield-4" cx="40%" cy="38%" r="76%">
          <stop offset="0%" stopColor="#D684B7" />
          <stop offset="100%" stopColor="#832573" />
        </radialGradient>
        <radialGradient id="login-shield-5" cx="18%" cy="56%" r="58%">
          <stop offset="0%" stopColor="#372D77" />
          <stop offset="68%" stopColor="#455CA4" />
          <stop offset="100%" stopColor="#455CA4" />
        </radialGradient>
        <radialGradient id="login-shield-6" cx="74%" cy="67%" r="56%">
          <stop offset="0%" stopColor="#842573" />
          <stop offset="100%" stopColor="#D684B7" />
        </radialGradient>
      </defs>
      <path
        d="M16 30C11.5 28.6 8 24.8 6.5 20.5C5.6 18.2 5.1 15.7 4.8 13.2C4.8 12.6 4.4 6.4 4.7 6.4C8.7 6.3 12.6 5.3 16 3"
        fill="url(#login-shield-1)"
      />
      <path
        d="M16 30C20.5 28.6 24 24.8 25.5 20.5C26.4 18.2 26.9 15.7 27.2 13.2C27.2 12.6 27.6 6.4 27.3 6.4C23.3 6.3 19.4 5.3 16 3"
        fill="url(#login-shield-2)"
      />
      <path
        d="M4.7 6.4C4.7 6.4 7.2 6.4 10 5.7C10 5.7 10.1 18 16 21.5V30C16 30 4.6 27.7 4.6 8C4.6 6.3 4.7 6.4 4.7 6.4Z"
        fill="url(#login-shield-3)"
      />
      <path
        d="M27.3 6.4C26.7 6.3 24.9 6.4 22 5.7C22 5.7 21.9 18 16 21.5V30C16 30 27.5 27.4 27.4 8C27.4 7.9 27.4 7.8 27.4 7.7C27.4 6.4 27.3 6.4 27.3 6.4Z"
        fill="url(#login-shield-4)"
      />
      <path
        d="M4.7 6.4C4.7 6.4 7.2 6.4 10 5.7C10 5.7 10.1 18 16 21.5V30C16 30 4.6 27.7 4.6 8C4.6 6.3 4.7 6.4 4.7 6.4Z"
        fill="url(#login-shield-5)"
        opacity="0.7"
      />
      <path
        d="M27.3 6.4C26.7 6.3 24.9 6.4 22 5.7C22 5.7 21.9 18 16 21.5V30C16 30 27.5 27.4 27.4 8C27.4 7.9 27.4 7.8 27.4 7.7C27.4 6.4 27.3 6.4 27.3 6.4Z"
        fill="url(#login-shield-6)"
        opacity="0.7"
      />
      <path
        d="M22.5 11.5L22.6 11.7C21.2 13 19.9 14.4 18.7 15.9C17 18.3 15.4 21 14 23.8H13.8L9.8 18.9L9.9 18.7H10L13.4 20.6C14.8 18.7 16.3 16.8 18 15.2C18.8 14.4 19.6 13.7 20.4 13C21.2 12.4 21.9 11.9 22.5 11.5Z"
        fill="white"
        style={{ filter: "drop-shadow(1px 2px 2px rgba(0,0,0,0.47))" }}
      />
    </svg>
  );
}

export function Login() {
  const navigate = useNavigate();
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);

  // Clear session-expired flags AND any lingering toasts from the previous
  // session — otherwise a "Session Ended" toast can still be on screen when
  // the user submits and hits "Invalid credentials", making both appear
  // together.
  useEffect(() => {
    sessionStorage.removeItem("logoutReason");
    sessionStorage.removeItem("_sessionExpiredFired");
    useToastStore.getState().clearToasts();
  }, []);

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    console.log("[Login] handleSubmit called");
    e.preventDefault();
    if (!username.trim() || !password.trim()) {
      console.log("[Login] Empty username or password");
      return;
    }

    console.log("[Login] Starting authentication");
    setLoading(true);

    try {
      console.log("[Login] Calling authentication");
      await authentication(username.trim(), password);
      console.log("[Login] Authentication successful");
      const permissionsResponse = await fetchUserPermissions();
      console.log("[Login] Permissions fetched:", permissionsResponse.data);
      useAuthStore.getState().setPermissions(permissionsResponse.data);
      console.log("[Login] Permissions stored in authStore");
      navigate("/dashboard/executive-insights", { replace: true });
    } catch (err: any) {
      console.log("[Login] Caught error:", err?.message);
      // Surface the backend error envelope (title + detail) in an error toast.
      const { title, message } = getErrorTitleAndMessage(
        err,
        "Login Failed",
        "Login failed. Please try again."
      );
      useToastStore.getState().showError(title, message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="relative h-screen w-screen overflow-hidden bg-[#0a1030]">
      {/* Background image — fills the viewport with object-cover so it never
          squishes, and object-top keeps the title/top visible (never cropped
          off). Normal screens fit exactly with no scrollbar; when the height
          shrinks (DevTools) the image re-crops proportionally instead of
          distorting, and the form panel scrolls on its own. */}
      <img
        src={loginBg}
        alt="Continuous Compliance Platform - Powered by OSCAL"
        className="absolute inset-0 w-full h-full object-cover object-top"
      />

      {/* Right panel gradient overlay */}
      <div
        className="absolute top-0 right-0 h-full"
        style={{
          width: "35.8%",
          background:
            "linear-gradient(180deg, white 25.7%, rgba(234, 234, 234, 0.57) 67.5%, rgba(255, 255, 255, 0) 93.4%)",
        }}
      />

      {/* Right panel - form container pinned to the image's white area.
          `my-auto` centers the form vertically when it fits but lets it scroll
          cleanly (no clipping) when the viewport is too short — e.g. DevTools
          open — instead of cutting off the Sign in button. */}
      <div
        className="absolute top-0 right-0 h-full flex justify-center overflow-y-auto"
        style={{ width: "35.8%" }}
      >
        <div
          className="flex flex-col items-center my-auto py-8"
          style={{ width: 403, gap: 40 }}
        >
          {/* Logo */}
          <div className="flex items-center justify-center gap-0.5">
            <RegoShield size={62} />
            <span
              className="font-bold bg-gradient-to-r from-[#2d1b69] to-[#4a3a8a] bg-clip-text text-transparent"
              style={{
                fontFamily: "Roboto, 'Segoe UI', system-ui, sans-serif",
                fontSize: 32,
              }}
            >
              RegO
            </span>
          </div>

          {/* Form section */}
          <div
            className="flex flex-col items-center w-full"
            style={{ gap: 22 }}
          >
            {/* Title */}
            <h2
              className="w-full text-center font-semibold"
              style={{
                fontFamily: "Roboto, 'Segoe UI', system-ui, sans-serif",
                fontSize: 22,
                lineHeight: "100%",
                color: "#2D308F",
              }}
            >
              Sign in to RegO
            </h2>

            {/* Card */}
            <form
              onSubmit={handleSubmit}
              className="w-full flex flex-col items-end"
              style={{
                padding: "48px 24px",
                gap: 24,
                background:
                  "linear-gradient(0deg, #FFFFFF, #FFFFFF), linear-gradient(164.38deg, #FFFFFF 23.86%, rgba(255, 255, 255, 0.43) 120.69%)",
                boxShadow:
                  "2px -2px 2px rgba(0, 0, 0, 0.25), -2px 2px 2px rgba(0, 0, 0, 0.25), -2px -2px 2px rgba(0, 0, 0, 0.25), 2px 2px 2px rgba(0, 0, 0, 0.25)",
                borderRadius: 8,
              }}
            >
              {/* Username field */}
              <div className="w-full flex flex-col" style={{ gap: 6 }}>
                <label
                  className="w-full font-semibold capitalize"
                  style={{
                    fontFamily: "Roboto, 'Segoe UI', system-ui, sans-serif",
                    fontSize: 16,
                    lineHeight: "100%",
                    color: "#1A2353",
                  }}
                >
                  Username
                </label>
                <input
                  type="text"
                  value={username}
                  onChange={(e) => setUsername(e.target.value)}
                  placeholder="Enter your username"
                  autoComplete="off"
                  spellCheck={false}
                  autoCorrect="off"
                  autoCapitalize="none"
                  className="w-full outline-none focus:border-[#2D308F] transition-colors"
                  style={{
                    padding: "12px 16px",
                    height: 48,
                    border: "1px solid #D0D5DD",
                    borderRadius: 8,
                    fontFamily: "Roboto, 'Segoe UI', system-ui, sans-serif",
                    fontSize: 14,
                    fontWeight: 400,
                    color: "#1A2353",
                    backgroundColor: "white",
                    boxSizing: "border-box",
                  }}
                />
              </div>

              {/* Password field. The extra bottom margin keeps the breathing
                * room the removed "Remember Me / Forgot Password" row used to
                * provide, so the Sign in button doesn't crowd the field. */}
              <div className="w-full flex flex-col" style={{ gap: 6, marginBottom: 16 }}>
                <div className="flex flex-col" style={{ gap: 12 }}>
                  <label
                    className="w-full font-semibold capitalize"
                    style={{
                      fontFamily: "Roboto, 'Segoe UI', system-ui, sans-serif",
                      fontSize: 16,
                      lineHeight: "100%",
                      color: "#1A2353",
                    }}
                  >
                    Password
                  </label>
                  <div className="relative w-full">
                    <input
                      type={showPassword ? "text" : "password"}
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      placeholder="Enter your password"
                      autoComplete="off"
                      spellCheck={false}
                      autoCorrect="off"
                      autoCapitalize="none"
                      className="w-full outline-none focus:border-[#2D308F] transition-colors"
                      style={{
                        padding: "12px 48px 12px 16px",
                        height: 48,
                        border: "1px solid #D0D5DD",
                        borderRadius: 8,
                        fontFamily: "Roboto, 'Segoe UI', system-ui, sans-serif",
                        fontSize: 14,
                        fontWeight: 400,
                        color: "#1A2353",
                        backgroundColor: "white",
                        boxSizing: "border-box",
                      }}
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="flex items-center justify-center"
                      style={{
                        position: "absolute",
                        right: 12,
                        top: "50%",
                        transform: "translateY(-50%)",
                        width: 24,
                        height: 24,
                        padding: 0,
                        border: "none",
                        background: "transparent",
                        cursor: "pointer",
                        color: "#3B3D40",
                      }}
                    >
                      {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
                    </button>
                  </div>
                </div>
              </div>

              {/* Error message */}
              {/* {error && (
                <p
                  className="w-full text-center"
                  style={{
                    fontFamily: "'Montserrat', sans-serif",
                    fontSize: 13,
                    color: "#DC2626",
                    fontWeight: 500,
                  }}
                >
                  {error}
                </p>
              )} */}

              {/* Sign in button & tagline */}
              <div className="w-full flex flex-col" style={{ gap: 12 }}>
                <button
                  type="submit"
                  disabled={loading}
                  className="w-full flex items-center justify-center hover:opacity-90 transition-opacity disabled:opacity-70 disabled:cursor-not-allowed"
                  style={{
                    padding: 16,
                    height: 48,
                    background: "#2D308F",
                    borderRadius: 8,
                    fontFamily: "Roboto, 'Segoe UI', system-ui, sans-serif",
                    fontSize: 18,
                    fontWeight: 600,
                    color: "white",
                    border: "none",
                    cursor: loading ? "not-allowed" : "pointer",
                  }}
                >
                  {loading ? (
                    <span className="flex items-center gap-2">
                      <span
                        className="inline-block animate-spin"
                        style={{
                          width: 16,
                          height: 16,
                          border: "2px solid rgba(255, 255, 255, 0.3)",
                          borderTopColor: "white",
                          borderRadius: "50%",
                        }}
                      />
                      Signing in...
                    </span>
                  ) : (
                    "Sign in"
                  )}
                </button>
                <p
                  className="w-full text-center  font-semibold"
                  style={{
                    fontFamily: "Roboto, 'Segoe UI', system-ui, sans-serif",
                    fontSize: 13,
                    lineHeight: "100%",
                    color: "#522CA8",
                  }}
                >
                  Your gateway to continuous compliance
                </p>
              </div>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
}
